import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import classNames from 'classnames';
import config from '@/config';
import api from '@/shared/api';
import AuthService, { functionalities } from '@/shared/services/AuthService';
import DataCacheService from '@/shared/services/DataCacheService';
import EnumCacheService from '@/shared/services/EnumCacheService';
import userStorage from '@/shared/services/userStorage';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import { retrieveAllResources } from '@/shared/components/registration/resourceApi';
import UserSearch from '@/shared/components/input/UserSearch';
import RelationSearch from '@/shared/components/input/RelationSearch';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import { TaskTemplateMultiSelect } from '@/shared/components/input/multiselect/TaskTemplateMultiSelect';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import FormattedListValue from '@/shared/components/FormattedListValue';
import DynamicFilterBase, { handleDynamicFiltersChanged, initDynamicFilters, resetDynamicFilters, } from '@/shared/components/DynamicFilter';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import messages from './messages';
import { computePeriodRange, createDefaultFilterState, } from './registrationOverviewFilterState';
// DynamicFilter is untyped JS; its inferred PropTypes are too strict for our extra props.
const DynamicFilter = DynamicFilterBase;
const CRM_TYPES = [2, 3, 4];
const DYNAMIC_FILTERS_STORAGE_KEY = 'RegistrationOverviewDynamicFilters';
const momentToUtcDate = (m) => m ? moment.utc(m.format('YYYY-MM-DD'), 'YYYY-MM-DD').toDate() : null;
const dateToMoment = (d) => (d ? moment.utc(d) : null);
const formatIncludeInactiveLabel = (option, intl) => {
    const msg = { id: `inactiverelationfilter_${option.key}`, defaultMessage: option.value };
    const translated = intl.formatMessage(msg);
    return translated === msg.id ? option.value : translated;
};
const toFilterObject = (s) => {
    var _a, _b;
    return ({
        withLabels: s.withLabels,
        withoutLabels: s.withoutLabels,
        withEmployeeLabels: s.withEmployeeLabels,
        withoutEmployeeLabels: s.withoutEmployeeLabels,
        ownCompanies: s.ownCompanies,
        legalEntities: s.legalEntities,
        endFiscalYears: s.endFiscalYears,
        relationGroups: s.relationGroups,
        assignmentTemplateId: s.assignmentTemplateId,
        referenceYear: s.referenceYear,
        relationHandlingLevels: s.relationHandlingLevels,
        relationTypes: s.relationTypes,
        invoiceDate: s.invoiceDateFrom || s.invoiceDateUntil ? 'set' : null,
        training: (_b = (_a = s.training) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null,
        isLocked: s.isLocked,
        includeInactive: s.includeInactive,
    });
};
const resetPatchForFilter = (id) => {
    if (id === 'invoiceDate')
        return { invoiceDateFrom: null, invoiceDateUntil: null };
    if (id === 'training')
        return { training: null };
    return { [id]: createDefaultFilterState()[id] };
};
const RegistrationOverviewFilters = ({ state, onChange, onApplyFilters, total, selectedCount, }) => {
    var _a;
    const intl = useIntl();
    const canViewOthers = AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
    });
    const canViewInvoiceFinal = AuthService.hasPermission({
        functionality: functionalities.InvoiceFinal,
    });
    const assignmentsEnabled = hasAssignments();
    const multiCompany = hasMultipleCompanies();
    const hasReferenceYear = AuthService.hasFeature('ReferenceYear');
    const hasTenantMoore = AuthService.hasFeature('TenantMoore');
    const allowRegistrationLocking = !!((_a = DataCacheService.getDefaultCompany()) === null || _a === void 0 ? void 0 : _a.allowRegistrationLocking);
    const [resources, setResources] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [includeInactiveOptions, setIncludeInactiveOptions] = useState([]);
    const [relationLabels, setRelationLabels] = useState([]);
    const [employeeLabels, setEmployeeLabels] = useState([]);
    useEffect(() => {
        let cancelled = false;
        const toLabels = (data) => (data || []).map(x => ({ id: x.id, name: x.name, label: x.name, color: x.color }));
        retrieveAllResources(intl).then(items => {
            if (!cancelled)
                setResources(items);
        });
        api.base.get(`${config.appUrls.api}/api/Training/Names`).then(({ data }) => {
            if (!cancelled)
                setTrainings((data || []).map((t) => ({ key: t.id, label: t.name })));
        });
        EnumCacheService.get('inactiveRelationFilter').then((options) => {
            if (!cancelled)
                setIncludeInactiveOptions(options || []);
        });
        api.base.get(`${config.appUrls.api}/api/resource/tags`).then(({ data }) => {
            if (!cancelled)
                setRelationLabels(toLabels(data));
        });
        if (canViewOthers) {
            api.base.get(`${config.appUrls.api}/api/employee/tags`).then(({ data }) => {
                if (!cancelled)
                    setEmployeeLabels(toLabels(data));
            });
        }
        return () => {
            cancelled = true;
        };
    }, [intl, canViewOthers]);
    const allDynamicFilters = useMemo(() => [
        { id: 'withLabels', label: messages.RelationsWithLabels },
        { id: 'withoutLabels', label: messages.RelationsWithoutLabels },
        { id: 'ownCompanies', label: messages.Companies, enabled: () => multiCompany },
        {
            id: 'legalEntities',
            label: messages.LegalEntities,
            enabled: () => multiCompany && hasTenantMoore,
        },
        { id: 'invoiceDate', label: messages.InvoiceDate, enabled: () => canViewInvoiceFinal },
        {
            id: 'assignmentTemplateId',
            label: messages.AssignmentTemplate,
            enabled: () => assignmentsEnabled,
        },
        { id: 'endFiscalYears', label: messages.EndFiscalYear },
        { id: 'relationGroups', label: messages.RelationGroups },
        { id: 'training', label: messages.Training },
        { id: 'referenceYear', label: messages.ReferenceYear, enabled: () => hasReferenceYear },
        {
            id: 'withEmployeeLabels',
            label: messages.WithEmployeeLabels,
            enabled: () => canViewOthers,
        },
        {
            id: 'withoutEmployeeLabels',
            label: messages.WithoutEmployeeLabels,
            enabled: () => canViewOthers,
        },
        { id: 'relationHandlingLevels', label: messages.HandlingLevels },
        { id: 'relationTypes', label: messages.RelationType },
        {
            id: 'isLocked',
            label: messages.LockedRegistrations,
            enabled: () => allowRegistrationLocking,
        },
        { id: 'includeInactive', label: messages.ActiveInactiveRelations },
    ], [
        multiCompany,
        hasTenantMoore,
        canViewInvoiceFinal,
        assignmentsEnabled,
        hasReferenceYear,
        canViewOthers,
        allowRegistrationLocking,
    ]);
    const [dynamicFilters, setDynamicFilters] = useState(() => initDynamicFilters(intl, allDynamicFilters, userStorage.getItem(DYNAMIC_FILTERS_STORAGE_KEY) || [], toFilterObject(state), toFilterObject(createDefaultFilterState())));
    const currentYear = useMemo(() => moment.utc().year(), []);
    const resourceValue = useMemo(() => resources.find(r => { var _a; return r.key === ((_a = state.resource) === null || _a === void 0 ? void 0 : _a.id); }) || null, [
        resources,
        state.resource,
    ]);
    const setPeriod = (period) => onChange(Object.assign({ period }, computePeriodRange(period)));
    const ignoreFilters = [
        'showAdvanceFilters',
        ...(state.period ? ['from', 'to'] : []),
        ...(canViewOthers ? [] : ['userId']),
    ];
    const savedFilterChanged = (filters) => {
        const toDate = (v) => (v ? new Date(v) : null);
        const next = Object.assign(Object.assign({}, createDefaultFilterState()), filters);
        next.from = toDate(next.from);
        next.to = toDate(next.to);
        next.invoiceDateFrom = toDate(next.invoiceDateFrom);
        next.invoiceDateUntil = toDate(next.invoiceDateUntil);
        if (next.period) {
            const range = computePeriodRange(next.period);
            next.from = range.from;
            next.to = range.to;
        }
        onApplyFilters(next);
        setDynamicFilters(prev => resetDynamicFilters(prev, toFilterObject(next), toFilterObject(createDefaultFilterState())));
    };
    const toggleCrmType = (crmType) => {
        var _a;
        (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur();
        const crmTypes = state.crmTypes.includes(crmType)
            ? state.crmTypes.filter(x => x !== crmType)
            : [...state.crmTypes, crmType];
        onChange({ crmTypes });
    };
    const relationChanged = (relation) => {
        var _a, _b, _c;
        const previousId = (_b = (_a = state.relation) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        const nextId = (_c = relation === null || relation === void 0 ? void 0 : relation.id) !== null && _c !== void 0 ? _c : null;
        onChange(Object.assign({ relation: relation ? Object.assign(Object.assign({}, relation), { id: nextId }) : null }, (previousId !== nextId ? { finalInvoiceStreamId: null } : {})));
    };
    const dynamicFilterChanged = (item, selected) => {
        const next = handleDynamicFiltersChanged(dynamicFilters, item, selected, toFilterObject(state), toFilterObject(createDefaultFilterState()));
        setDynamicFilters(next);
        userStorage.setItem(DYNAMIC_FILTERS_STORAGE_KEY, next.filter(x => x.selected).map(x => x.id));
        if (!selected && item.id !== 'selectall')
            onChange(resetPatchForFilter(item.id));
    };
    const renderCustomFilter = ({ type, filters, filterChanged, collClass }) => {
        var _a;
        if (type === 'invoiceDate') {
            return (_jsx("div", Object.assign({ className: collClass }, { children: _jsxs("div", Object.assign({ className: "form-group row" }, { children: [_jsx("label", Object.assign({ className: "control-label col-xs-4" }, { children: intl.formatMessage(messages.InvoiceDate) })), _jsx("div", Object.assign({ className: "col-xs-8" }, { children: _jsx(DelayedDateRangePicker, { startDate: dateToMoment(filters.invoiceDateFrom), endDate: dateToMoment(filters.invoiceDateUntil), onDatesChange: ({ startDate, endDate }) => filterChanged({
                                    invoiceDateFrom: momentToUtcDate(startDate),
                                    invoiceDateUntil: momentToUtcDate(endDate),
                                }), startDatePlaceholderText: intl.formatMessage(messages.From), endDatePlaceholderText: intl.formatMessage(messages.Until), block: true }) }))] })) })));
        }
        if (type === 'training') {
            const value = trainings.find(t => { var _a; return t.key === ((_a = filters.training) === null || _a === void 0 ? void 0 : _a.id); }) || null;
            return (_jsx("div", Object.assign({ className: collClass }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx(AutoComplete, { items: trainings, value: value, mode: "edit", placeholder: intl.formatMessage(messages.SelectTraining), onChange: (itm) => filterChanged({ training: itm ? { id: itm.key } : null }) }) })) })));
        }
        if (type === 'isLocked') {
            return (_jsx("div", Object.assign({ className: collClass }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsxs("div", Object.assign({ role: "group", "aria-label": "locked", className: "btn-group btn-group-justified" }, { children: [_jsx("button", Object.assign({ type: "button", className: classNames('btn btn-default', { active: filters.isLocked == null }), onClick: () => filterChanged({ isLocked: null }) }, { children: intl.formatMessage(messages.FilterLockedRegistrationsAll) })), _jsxs("button", Object.assign({ type: "button", className: classNames('btn btn-default', { active: filters.isLocked === false }), onClick: () => filterChanged({ isLocked: false }) }, { children: [_jsx("i", { className: "fa fa-unlock fa-fw margin5R" }), intl.formatMessage(messages.FilterLockedRegistrationsUnlocked)] })), _jsxs("button", Object.assign({ type: "button", className: classNames('btn btn-default', { active: filters.isLocked === true }), onClick: () => filterChanged({ isLocked: true }) }, { children: [_jsx("i", { className: "fa fa-lock fa-fw margin5R" }), intl.formatMessage(messages.FilterLockedRegistrationsLocked)] }))] })) })) })));
        }
        if (type === 'includeInactive') {
            return (_jsx("div", Object.assign({ className: collClass }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsxs("select", Object.assign({ className: "form-control", value: (_a = filters.includeInactive) !== null && _a !== void 0 ? _a : '', onChange: e => filterChanged({
                            includeInactive: e.target.value === '' ? null : Number(e.target.value),
                        }) }, { children: [_jsx("option", Object.assign({ value: "" }, { children: intl.formatMessage(messages.ActiveInactiveRelations) })), includeInactiveOptions.map(option => (_jsx("option", Object.assign({ value: option.key }, { children: formatIncludeInactiveLabel(option, intl) }), option.key)))] })) })) })));
        }
        return null;
    };
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);
    const periodButtons = [
        { key: 'yesterday', label: intl.formatMessage(messages.Yesterday) },
        { key: 'today', label: intl.formatMessage(messages.Today) },
        { key: 'prevWeek', label: intl.formatMessage(messages.PreviousWeek) },
        { key: 'week', label: intl.formatMessage(messages.ThisWeek) },
        { key: 'prevMonth', label: intl.formatMessage(messages.PreviousMonth) },
        { key: 'month', label: intl.formatMessage(messages.ThisMonth) },
        { key: 'prevQuarter', label: intl.formatMessage(messages.PreviousQuarter) },
        { key: 'quarter', label: intl.formatMessage(messages.ThisQuarter) },
        { key: 'prevYear', label: currentYear - 1 },
        { key: 'year', label: currentYear },
        { key: 'all', label: intl.formatMessage(messages.All) },
    ];
    return (_jsxs("div", Object.assign({ className: "container-fluid info-block" }, { children: [_jsxs("div", Object.assign({ className: "row" }, { children: [canViewOthers && (_jsx("div", Object.assign({ className: "col-lg-4" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx(UserSearch, { value: state.userId, onChange: (user) => onChange({ userId: user ? user.id : null }), withSystemUser: true, placeholder: intl.formatMessage(messages.AllUsers) }) })) }))), _jsx("div", Object.assign({ className: "col-md-6 col-lg-4" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx(AutoComplete, { items: resources, value: resourceValue, mode: "edit", placeholder: intl.formatMessage(messages.SelectResource), onChange: (item) => onChange({ resource: item ? { id: item.key } : null }) }) })) })), _jsx("div", Object.assign({ className: "col-md-6 col-lg-4" }, { children: _jsxs("div", Object.assign({ className: "form-group row" }, { children: [_jsx("div", Object.assign({ className: "col-sm-4" }, { children: _jsx("label", { children: intl.formatMessage(messages.Period) }) })), _jsx("div", Object.assign({ className: "col-sm-8" }, { children: _jsx(DelayedDateRangePicker, { startDate: dateToMoment(state.from), endDate: dateToMoment(state.to), onDatesChange: ({ startDate, endDate }) => onChange({
                                            period: '',
                                            from: momentToUtcDate(startDate),
                                            to: momentToUtcDate(endDate),
                                        }), block: true }) }))] })) }))] })), _jsxs("div", Object.assign({ className: "row" }, { children: [_jsx("div", Object.assign({ className: "col-lg-4" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx(RelationSearch, { initialSelectedItem: state.relation
                                    ? { key: state.relation.id, label: state.relation.label || '' }
                                    : null, crmTypes: [2, 3, 4], includeInactive: false, take: 20, onChange: relationChanged }) })) })), _jsx("div", Object.assign({ className: "col-md-6 col-lg-4" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx(TaskTemplateMultiSelect, { selection: state.taskTemplates || [], onChange: taskTemplates => onChange({ taskTemplates }) }) })) })), _jsx("div", Object.assign({ className: "col-md-6 col-lg-4" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx("div", Object.assign({ role: "group", "aria-label": "crmType", className: "btn-group btn-group-justified" }, { children: CRM_TYPES.map(crmType => (_jsx("button", Object.assign({ type: "button", className: classNames('btn btn-default', {
                                        active: state.crmTypes.includes(crmType),
                                    }), onClick: () => toggleCrmType(crmType) }, { children: _jsx(FormattedListValue, { list: "crmtype", id: crmType }) }), crmType))) })) })) }))] })), visibleFilters.length > 0 && (_jsx("div", Object.assign({ className: "row flex-row" }, { children: visibleFilters.map(id => (_jsx(DynamicFilter, { type: id, filters: state, filterChanged: onChange, intl: intl, labels: relationLabels, employeeLabels: employeeLabels, labelsIdProp: "name", collClass: "col-md-4", placeholders: {
                        withLabels: intl.formatMessage(messages.RelationsWithLabels),
                        withoutLabels: intl.formatMessage(messages.RelationsWithoutLabels),
                    }, renderCustomFilter: renderCustomFilter }, id))) }))), _jsxs("div", Object.assign({ className: "row" }, { children: [_jsx("div", Object.assign({ className: "col-md-12" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx("div", Object.assign({ role: "group", "aria-label": "period", className: "btn-group btn-group-justified" }, { children: periodButtons.map(({ key, label }) => (_jsx("button", Object.assign({ type: "button", className: classNames('btn btn-default', { active: state.period === key }), onClick: () => setPeriod(key) }, { children: label }), key))) })) })) })), _jsx("div", Object.assign({ className: "col-md-6" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx(SavedFiltersButton, { className: "btn-group", screen: "RegistrationOverview", currentFilters: state, defaultFilters: createDefaultFilterState(), onFiltersChanged: savedFilterChanged, ignoreFilters: ignoreFilters, dynamicFilters: dynamicFilters, onDynamicFiltersChanged: dynamicFilterChanged }) })) })), _jsx("div", Object.assign({ className: "col-md-6" }, { children: _jsx("div", Object.assign({ className: "form-group text-right" }, { children: _jsx("div", Object.assign({ className: "margin20R margin10T" }, { children: intl.formatMessage(messages.RegistrationsSelected, {
                                    selected: selectedCount,
                                    total,
                                }) })) })) }))] }))] })));
};
export default RegistrationOverviewFilters;
